import _ from 'lodash';
import React, { FC, useCallback, useState } from 'react';

import Actions, {
  ActionType,
  AddTopicsAction as AddTopicsActionType,
  CreateTaskAction as CreateTaskActionType,
  invalidActionsForTrigger,
  RouteToAgentAction as RouteToAgentActionType,
  RouteToGroupAction as RouteToGroupActionType,
  SendReplyAction as SendReplyActionType,
  SetPriorityAction as SetPriorityActionType,
} from 'src/actions/rules/actions';
import AddTopicsAction from './actions/AddTopicsAction';
import AddNoReplyNeededAction from './actions/AddNoReplyNeededAction';
import CloseConversationAction from './actions/CloseConversationAction';
import CreateTaskAction from './actions/CreateTaskAction';
import { getGroupedMenuOptions } from './shared/getGroupedMenuOptions';
import RouteToAgentAction from './actions/RouteToAgentAction';
import RouteToGroupAction from './actions/RouteToGroupAction';
import RuleEditorSection, { AddRuleRequirementButton, StyledDropdown, StyledSelectorRow } from './RuleEditorSection';
import RuleSectionErrors from './shared/RuleSectionErrors';
import SendReplyAction from './actions/SendReplyAction';
import SetPriorityAction from './actions/SetPriorityAction';
import { StyledRowWrapper } from './shared/StyledComponents';
import { TriggerType } from 'src/actions/rules/trigger';
import UnassignAgentAction from './actions/UnassignAgentAction';

type RuleActionsType = {
  actions: Actions;
  title: string;
  errors: any;
  triggerType: TriggerType;
  onAddAction: Function;
  onDeleteAction: Function;
  onUpdateAction: Function;
};

enum ActionGroup {
  Routing = 'Routing',
  AddToConversation = 'Add To Conversation',
  Cancel = 'Cancel',
  Task = 'Task',
}

const RuleActions: FC<RuleActionsType> = ({
  actions,
  title,
  errors,
  triggerType,
  onAddAction,
  onDeleteAction,
  onUpdateAction,
}) => {
  const [hasNewAction, setHasNewAction] = useState(false);

  const onSelectActionType = useCallback(
    type => {
      onAddAction(type, true);
      setHasNewAction(false);
    },
    [onAddAction, setHasNewAction]
  );
  const onInitiateAction = useCallback(() => {
    setHasNewAction(true);
  }, [setHasNewAction]);

  const invalidActions = invalidActionsForTrigger[triggerType];
  const actionMenuOptions = actionOptions.filter(({ value }) => {
    if (actions[value] || _.indexOf(invalidActions, value) !== -1) {
      // don't include actions that are already configured
      return false;
    }

    // UnassignAgent and RouteToAgent are mutually exclusive
    if (value === ActionType.UnassignAgent) {
      return !actions[ActionType.RouteToAgent];
    } else if (value === ActionType.RouteToAgent) {
      return !actions[ActionType.UnassignAgent];
    }

    return true;
  });

  const groupedActionMenuOptions = getGroupedMenuOptions(actionMenuOptions, ActionGroup);

  const actionsToDisplay = useDisplayOptions(actions, actionOptions);

  return (
    <RuleEditorSection title={title}>
      <div>
        {_.map(actionsToDisplay, key => (
          <StyledRowWrapper key={key}>
            <RuleAction key={key} name={key} value={actions[key]} onDelete={onDeleteAction} onUpdate={onUpdateAction} />
            <RuleSectionErrors errors={errors} attr={`actions.${key}`} />
          </StyledRowWrapper>
        ))}
      </div>
      {renderActionSelector()}
      {renderAddButton()}
      {renderMissingActionError()}
    </RuleEditorSection>
  );

  function renderMissingActionError() {
    for (const name in actions) {
      if (actions[name] !== undefined) return null;
    }
    return <RuleSectionErrors errors={errors} attr={'actions'} />;
  }

  function renderActionSelector() {
    if (!hasNewAction || groupedActionMenuOptions.length === 0) return null;

    return (
      <StyledSelectorRow>
        Then
        <StyledDropdown
          attr="actions"
          data-aid="ruleActions-selectButton"
          onSelect={onSelectActionType}
          options={groupedActionMenuOptions}
          placeholder="Select action"
          searchable
          value={''}
        />
      </StyledSelectorRow>
    );
  }

  function renderAddButton() {
    if (groupedActionMenuOptions.length === 0) return null;
    return <AddRuleRequirementButton data-aid="ruleActions-addButton" onClick={onInitiateAction} text="Add Action" />;
  }
};

const actionOptions: { label: string; value: string; group: string }[] = [
  { label: 'Add Topics', value: ActionType.AddTopics, group: ActionGroup.AddToConversation },
  { label: 'Assign to Inbox', value: ActionType.RouteToGroup, group: ActionGroup.Routing },
  { label: 'Assign to Agent', value: ActionType.RouteToAgent, group: ActionGroup.Routing },
  { label: 'Close Conversation', value: ActionType.CloseConversation, group: ActionGroup.Cancel },
  { label: 'Create a Task', value: ActionType.CreateTask, group: ActionGroup.Task },
  { label: 'Mark No Reply Needed', value: ActionType.AddNoReplyNeeded, group: ActionGroup.Cancel },
  { label: 'Send Auto-Reply', value: ActionType.SendReply, group: ActionGroup.AddToConversation },
  { label: 'Set Priority', value: ActionType.SetPriority, group: ActionGroup.Routing },
  { label: 'Unassign from Agent', value: ActionType.UnassignAgent, group: ActionGroup.Routing },
];

type RuleActionType = {
  name: string;
  onDelete: Function;
  onUpdate: Function;
  value: any;
};

const RuleAction: FC<RuleActionType> = ({ name, value, onDelete, onUpdate }) => {
  switch (name) {
    case ActionType.AddNoReplyNeeded:
      return <AddNoReplyNeededAction onDelete={onDelete} />;
    case ActionType.AddTopics:
      return <AddTopicsAction action={value as AddTopicsActionType} onDelete={onDelete} onUpdate={onUpdate} />;
    case ActionType.CloseConversation:
      return <CloseConversationAction onDelete={onDelete} />;
    case ActionType.CreateTask:
      return <CreateTaskAction action={value as CreateTaskActionType} onDelete={onDelete} onUpdate={onUpdate} />;
    case ActionType.RouteToAgent:
      return <RouteToAgentAction action={value as RouteToAgentActionType} onDelete={onDelete} onUpdate={onUpdate} />;
    case ActionType.RouteToGroup:
      return <RouteToGroupAction action={value as RouteToGroupActionType} onDelete={onDelete} onUpdate={onUpdate} />;
    case ActionType.SendReply:
      return <SendReplyAction action={value as SendReplyActionType} onDelete={onDelete} onUpdate={onUpdate} />;
    case ActionType.SetPriority:
      return <SetPriorityAction action={value as SetPriorityActionType} onUpdate={onUpdate} />;
    case ActionType.UnassignAgent:
      return <UnassignAgentAction onDelete={onDelete} />;
    default:
      return null;
  }
};

function useDisplayOptions(actions, actionOptions) {
  return actionOptions.filter(({ value }) => !!actions[value]).map(({ value }) => value);
}

export default RuleActions;
